import { makeStyles, Tab, Tabs } from '@material-ui/core';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { updateSelectedTab } from '../../redux/actions/menuTabs';
import { tabStyles } from '../Projects/ProjectsAll/ProjectsAll.Style';
import { AppMenuTabs, MenuTabs, MenuTabSectionEnum } from './userTabsInterface';
import { AddMenuTab } from './AddMenuTab/AddMenuTab';
import { SeparatorMenu } from './SeparetorMenu/SeparatorMenu';
import { TrackedActions } from '../../models/TrackedActionsEnum';
import { setFilterIndicators } from '../../redux/actions/filterIndicators';
import { trackActionClicked } from '../../api/trackActionClickedApi';
import { TagFilterContext } from '../../context/TagFilterContext';
import './MenuTabsComponent.scss';
import SelectOption from '../../models/SelectOption';

type Props = {
  menuTabSection: MenuTabSectionEnum;
  selectedTab: MenuTabs;
  handleAddTabButton?: () => void;
};

export const MenuTabsComponent = ({ menuTabSection, selectedTab, handleAddTabButton }: Props) => {
  tabStyles.root.backgroundColor = 'transparent';
  tabStyles.root['&$selected'].backgroundColor = 'transparent';
  const classes = makeStyles(tabStyles)();
  const tabClasses = { root: classes.root, selected: classes.selected };
  const { labels, setLabels, setFlattenedOptions, deleteTagFromContext } = useContext(TagFilterContext);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const maxNumberOfCustomTabs = 4;
  const isLoading = useSelector<any, any>(state => state.dataTableInfo.loading);
  const { userTabs } = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const roleBasedMenu: MenuTabs[] = userTabs.filter(m => m.tabId === 0);
  const customUserMenu: MenuTabs[] = userTabs.filter(m => m.tabId !== 0);
  const regionsState = useSelector<any, any>(state => state.regions) || [];
  const sectorsState = useSelector<any, any>(state => state.sectors) || [];
  const divisionsState = useSelector<any, any>(state => state.divisions) || [];

  const filterIndicators = useSelector<any, any>(state => state.filterIndicators);
  const manualFiltersRef = useRef({
    regionId: filterIndicators.common.regionId,
    countryId: filterIndicators.common.countryId,
    sectors: filterIndicators.common.sectors,
    divisionId: filterIndicators.common.divisionId,
  });
  const suppressManualUpdateRef = useRef(false);

  useEffect(() => {
    if (suppressManualUpdateRef.current) {
      suppressManualUpdateRef.current = false;
      return;
    }
    manualFiltersRef.current = {
      regionId: filterIndicators.common.regionId,
      countryId: filterIndicators.common.countryId,
      sectors: filterIndicators.common.sectors,
      divisionId: filterIndicators.common.divisionId,
    };
  }, [
    filterIndicators.common.regionId,
    filterIndicators.common.countryId,
    filterIndicators.common.sectors,
    filterIndicators.common.divisionId,
  ]);

  const mapRegionsToSelectOptions = (regionIds: number[]): SelectOption[] => {
    if (!regionIds?.length) return [];
    return regionIds
      .map(id => {
        const region = regionsState.find(r => r.id === id);
        return region
          ? {
            id: region.id,
            label: region.acronym,
            value: region.acronym,
          }
          : null;
      })
      .filter((option): option is SelectOption => Boolean(option));
  };

  const mapCountriesToSelectOptions = (countryIds: number[]): SelectOption[] => {
    if (!countryIds?.length) return [];
    const countries: SelectOption[] = [];
    regionsState.forEach(region => {
      region.countries.forEach(country => {
        if (countryIds.includes(country.id)) {
          countries.push({
            id: country.id,
            label: country.name,
            value: country.name,
            categoryId: country.regionId,
          });
        }
      });
    });
    return countries;
  };

  const getFilterValues = (menu: MenuTabs, field: string) => {
    const filter = menu.filters?.find(f => f.field === field);
    return filter?.value ?? [];
  };

  const mapSectorsToSelectOptions = (sectorIds: number[]): SelectOption[] => {
    if (!sectorIds?.length) return [];
    return sectorIds
      .map(id => {
        const sector = sectorsState.find(s => s.id === id);
        return sector
          ? {
            id: sector.id,
            label: sector.description,
            value: sector.code,
          }
          : null;
      })
      .filter((option): option is SelectOption => Boolean(option));
  };

  const mapDivisionsToSelectOptions = (divisionIds: number[]): SelectOption[] => {
    if (!divisionIds?.length) return [];
    return divisionIds
      .map(id => {
        const division =
          divisionsState.find(d => d.id === id) ||
          sectorsState
            .flatMap(sector => sector.divisions || [])
            .find((divisionItem: any) => divisionItem.id === id);
        return division
          ? {
            id: division.id,
            label: `${division.code ? `${division.code} - ` : ''}${division.name}`,
            value: division.name,
          }
          : null;
      })
      .filter((option): option is SelectOption => Boolean(option));
  };

  const resetPortfolioFiltersFromTab = (menu: MenuTabs) => {
    if (menuTabSection !== MenuTabSectionEnum.PORTFOLIO_MENU_TAB) return;
    const baseFilters = { regionId: [], countryId: [], sectors: [], divisionId: [] };

    const updatedCommon = {
      ...filterIndicators.common,
      regionId: baseFilters.regionId,
      countryId: baseFilters.countryId,
      sectors: baseFilters.sectors,
      divisionId: baseFilters.divisionId,
      coReponsibleDivisions: [],
      participantDivisions: [],
      teamLeaders: [],
      interestIssues: [],
    };

    const regionsSelected = mapRegionsToSelectOptions(menu.regions || []);
    if (regionsSelected.length) {
      updatedCommon.regionId = regionsSelected;
    }

    const countriesSelected = mapCountriesToSelectOptions(getFilterValues(menu, 'ResponsibleCountryId'));
    if (countriesSelected.length) {
      updatedCommon.countryId = countriesSelected;
    }

    const sectorsSelected = mapSectorsToSelectOptions(getFilterValues(menu, 'SectorId'));
    if (sectorsSelected.length) {
      updatedCommon.sectors = sectorsSelected;
    }

    const divisionsSelected = mapDivisionsToSelectOptions(getFilterValues(menu, 'DivisionId'));
    if (divisionsSelected.length) {
      updatedCommon.divisionId = divisionsSelected;
    }

    suppressManualUpdateRef.current = true;
    dispatch(
      setFilterIndicators({
        ...filterIndicators,
        common: {
          ...updatedCommon,
        },
      }),
    );
  };

  const handleSetMenuValue = (menuValue: MenuTabs) => {
    trackActionClicked(TrackedActions.menuTabClicked);
    if (menuValue.internalId === selectedTab.internalId) {
      return;
    }
    dispatch(updateSelectedTab(menuValue, menuTabSection));
    if (menuTabSection === MenuTabSectionEnum.PORTFOLIO_MENU_TAB) {
      resetPortfolioFiltersFromTab(menuValue);
    } else if (menuValue.tabId !== 0) {
      const regionsSelected = mapRegionsToSelectOptions(menuValue.regions || []);
      const countriesSelected = mapCountriesToSelectOptions(getFilterValues(menuValue, 'ResponsibleCountryId'));
      suppressManualUpdateRef.current = true;
      dispatch(
        setFilterIndicators({
          ...filterIndicators,
          common: {
            ...filterIndicators.common,
            sectors: [],
            divisionId: [],
            coReponsibleDivisions: [],
            participantDivisions: [],
            regionId: regionsSelected,
            countryId: countriesSelected,
            teamLeaders: [],
            interestIssues: [],
          },
        }),
      );
    }
    setRegionTrackOnMenuTab(
      menuValue,
      selectedTab,
      menuTabSection === MenuTabSectionEnum.PORTFOLIO_MENU_TAB
        ? TrackedActions.regionFilterChangedFromPortfolio
        : TrackedActions.regionFilterChangedFromProject,
    );
  };

  const clearFilterTag = () => {
    labels.forEach(label => deleteTagFromContext(label));

    setLabels([]);
    setFlattenedOptions([]);
  };

  return (
    <>
      <Tabs
        value={selectedTab.internalId || 0}
        classes={{ indicator: classes.indicatorColor, scrollButtons: classes.scrollButtons }}
        variant="scrollable"
        aria-label="full width tabs example"
        scrollButtons="on"
      >
        {roleBasedMenu.map((menu: MenuTabs, i: number) => (
          <Tab
            key={menu.internalId}
            value={menu.internalId}
            classes={tabClasses}
            label={t(menu.title)}
            onClick={() => {
              handleSetMenuValue(menu);
            }}
            disabled={isLoading}
            tabIndex={20 + i}
            className="menu-tabs__tab"
          />
        ))}
        <SeparatorMenu menuTabSection={menuTabSection} />
        {customUserMenu.map((menu: MenuTabs) => (
          <Tab
            key={menu.internalId}
            value={menu.internalId}
            classes={tabClasses}
            label={sliceText(menu.title)}
            onClick={() => {
              handleSetMenuValue(menu);
              clearFilterTag();
            }}
            tabIndex={30}
            disabled={isLoading}
            className="menu-tabs__tab"
          />
        ))}
      </Tabs>
      {menuTabSection === MenuTabSectionEnum.PORTFOLIO_MENU_TAB && (
        <AddMenuTab
          disable={customUserMenu.length >= maxNumberOfCustomTabs}
          handleButtonClick={handleAddTabButton ? handleAddTabButton : () => { }}
        />
      )}
    </>
  );
};

const sliceText = (text: string) => {
  return text.length >= 10 ? `${text.slice(0, 10)}...` : text;
};

const setRegionTrackOnMenuTab = (
  userApplyedTabValueSelected: MenuTabs,
  reducerTabValueSelected: MenuTabs,
  trackedAction: TrackedActions,
) => {
  if (
    !userApplyedTabValueSelected.assignedToMe &&
    userApplyedTabValueSelected.internalId !== '0_global_bid' &&
    reducerTabValueSelected !== userApplyedTabValueSelected &&
    userApplyedTabValueSelected.tabId === 0 &&
    userApplyedTabValueSelected.filters.length === 0 &&
    userApplyedTabValueSelected.regions.length > 0
  ) {
    trackActionClicked(trackedAction);
  }
};
